import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Globe2, BookOpen, Sparkles, Music2, Briefcase, ExternalLink } from "lucide-react";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "./ui/command";
import { COUNTRY_AGENCIES } from "../data/countryAgencies";
import { RIGHTS_TOPICS } from "../data/rightsTopics";
import { BRANDING_NATIONAL, BRANDING_INTERNATIONAL, BRANDING_ANTHROPOLOGY } from "../data/brandingAgencies";
import { ORCHESTRATION_RESOURCES } from "../data/orchestrationResources";

// Build the global index once per session
function buildIndex() {
  const items = [];

  // 1. Country agencies — open external agency website
  COUNTRY_AGENCIES.forEach((c) => {
    items.push({
      id: `country-${c.cc}`,
      group: "Country agencies",
      label: `${c.agency} — ${c.country}`,
      sub: c.country,
      external: c.url,
      icon: Globe2,
    });
  });

  // 2. Rights topics (internal route)
  Object.entries(RIGHTS_TOPICS).forEach(([slug, t]) => {
    items.push({
      id: `topic-${slug}`,
      group: "Rights topics",
      label: t.title,
      sub: t.overline,
      route: `/rights-topic/${slug}`,
      icon: BookOpen,
    });
    // 2b. Resources inside each topic (external)
    (t.resources || []).forEach((r) => {
      items.push({
        id: `topic-${slug}-${r.name}`,
        group: "Rights resources",
        label: r.name,
        sub: t.title,
        external: r.url,
        icon: Sparkles,
      });
    });
  });

  // 3. Branding (all 3 tiers)
  const brandingTiers = [
    { label: "National Branding", entries: BRANDING_NATIONAL, route: "/branding/national" },
    { label: "International Branding", entries: BRANDING_INTERNATIONAL, route: "/branding/international" },
    { label: "Branding Anthropology", entries: BRANDING_ANTHROPOLOGY, route: "/branding/anthropology" },
  ];
  brandingTiers.forEach((tier) => {
    tier.entries.forEach((e) => {
      items.push({
        id: `branding-${tier.label}-${e.name}`,
        group: "Branding",
        label: e.name,
        sub: tier.label,
        external: e.website,
        icon: Briefcase,
      });
    });
  });

  // 4. Orchestration resources (external)
  ORCHESTRATION_RESOURCES.forEach((o) => {
    items.push({
      id: `orch-${o.name}`,
      group: "Orchestration",
      label: o.name,
      sub: o.category,
      external: o.website,
      icon: Music2,
    });
  });

  return items;
}

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const index = useMemo(() => buildIndex(), []);

  // Keyboard shortcut: Cmd/Ctrl+K
  useEffect(() => {
    const down = (e) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const handleSelect = (item) => {
    setOpen(false);
    if (item.route) {
      nav(item.route);
    } else if (item.external) {
      window.open(item.external, "_blank", "noopener,noreferrer");
    }
  };

  // Group items by group key (cmdk preserves order within groups)
  const grouped = useMemo(() => {
    const out = {};
    for (const it of index) {
      (out[it.group] = out[it.group] || []).push(it);
    }
    return out;
  }, [index]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] hover:border-[#7DA3D9]/40 transition-colors text-xs text-slate-400 min-w-[220px]"
        data-testid="global-search-trigger"
        title="Search the OSRR registry"
      >
        <Search className="w-3.5 h-3.5 text-[#7DA3D9]" />
        <span>Search agencies, topics, countries…</span>
        <span className="ml-auto inline-flex items-center gap-0.5 text-[10px] text-slate-500 border border-white/10 rounded px-1.5 py-0.5">
          <span>⌘</span><span>K</span>
        </span>
      </button>

      {/* Mobile icon-only trigger */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="md:hidden p-2 rounded-md hover:bg-white/5 transition-colors"
        data-testid="global-search-trigger-mobile"
        aria-label="Search"
      >
        <Search className="w-4 h-4 text-slate-300" />
      </button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Search 700+ agencies, rights topics, countries, branding firms…"
          data-testid="global-search-input"
        />
        <CommandList data-testid="global-search-results">
          <CommandEmpty>No results.</CommandEmpty>
          {Object.entries(grouped).map(([groupName, items]) => (
            <CommandGroup key={groupName} heading={groupName}>
              {items.map((it) => {
                const Icon = it.icon;
                return (
                  <CommandItem
                    key={it.id}
                    value={`${it.label} ${it.sub || ""}`}
                    onSelect={() => handleSelect(it)}
                    className="cursor-pointer"
                    data-testid={`search-result-${it.id}`}
                  >
                    <Icon className="text-[#7DA3D9]" />
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-sm text-white truncate">{it.label}</span>
                      {it.sub && (
                        <span className="text-[11px] text-slate-400 truncate">{it.sub}</span>
                      )}
                    </div>
                    {it.external && <ExternalLink className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
}
